import * as Api from '@ViewModels';
import { events } from 'fetch-event-stream';

export const IMAGE_BRANDING_ASSISTANT_NAME = 'ImageGenerationAgent';

const IMAGE_BRANDING_STREAM_ERROR_FALLBACK = 'Image branding failed';

/**
 * Best-effort message from an SSE `Error` event body (plain text or JSON with common message fields).
 */
function getImageBrandingStreamErrorMessage(raw: string): string {
	const trimmed = raw.trim();
	if (!trimmed) {
		return IMAGE_BRANDING_STREAM_ERROR_FALLBACK;
	}
	try {
		const parsed: unknown = JSON.parse(trimmed);
		if (typeof parsed === 'string' && parsed.trim()) {
			return parsed.trim();
		}
		if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
			const o = parsed as Record<string, unknown>;
			const nestedError = o.error;
			const fromNested =
				nestedError &&
				typeof nestedError === 'object' &&
				!Array.isArray(nestedError) &&
				typeof (nestedError as Record<string, unknown>).message === 'string'
					? String((nestedError as Record<string, unknown>).message).trim()
					: '';
			const direct =
				(typeof o.message === 'string' && o.message.trim()) ||
				(typeof o.error === 'string' && o.error.trim()) ||
				(typeof o.detail === 'string' && o.detail.trim()) ||
				(typeof o.title === 'string' && o.title.trim()) ||
				fromNested;
			if (direct) {
				return direct;
			}
		}
	} catch {
		// use raw payload below
	}
	return trimmed;
}

export interface BrandingSystemJobResult extends Api.ISystemJob {
	resultImages: {
		fileAttachmentId: string;
		mimeType: string;
		sourceFileAttachmentId: string;
	}[];
	metadata: {
		failedImages: number;
		totalImages: number;
		successfulImages: number;
	};
	sourceFileAttachmentIds: string[];
}

/**
 * Opens the SSE stream for ImageBrandingAction. The caller consumes the body (e.g. useStreamingResponseMutation).
 */
export function requestImageBrandingStream(params: {
	userSession: Api.UserSessionContext;
	conversationId: string;
	impersonationContext?: Api.IImpersonationContext;
	attachmentIds: string[];
}): Promise<Response> {
	const { userSession, conversationId, impersonationContext, attachmentIds } = params;
	return new Promise<Response>((resolve, reject) => {
		userSession.webServiceHelper.callWebServiceRawResponse(
			Api.ImpersonationBroker.composeApiUrl({
				urlPath: `ai/assistant/${IMAGE_BRANDING_ASSISTANT_NAME}/conversation/${conversationId}/stream`,
				impersonationContext,
			}),
			'POST',
			{
				_type: 'AIActionRequest',
				action: {
					_type: 'ImageBrandingAction' as Api.ApiModelType,
					sourceFileAttachmentIds: attachmentIds,
				},
			},
			resolve,
			reject
		);
	});
}

/**
 * Full stream read; returns the first system job id from ToolResponse events.
 * Used for parallel per-image branding where each stream maps to one job.
 */
export async function startImageBrandingStream(params: {
	userSession: Api.UserSessionContext;
	conversationId: string;
	impersonationContext?: Api.IImpersonationContext;
	attachmentIds: string[];
	signal?: AbortSignal;
}): Promise<string> {
	const response = await requestImageBrandingStream(params);
	let systemJobId: string | undefined;
	// AbortSignal stops local event iteration only; `callWebServiceRawResponse` has no signal overload,
	// so the HTTP response body may keep streaming until the server finishes or the connection times out.
	for await (const event of events(response, params.signal)) {
		if (event.event === 'Heartbeat') {
			continue;
		}

		if (event.event === 'Error') {
			throw new Error(getImageBrandingStreamErrorMessage(event.data ?? ''));
		}

		if (event.event !== 'ToolResponse') {
			continue;
		}
		try {
			const data = JSON.parse(event.data);
			const id = data?.toolCall?.systemJobId as string | undefined;
			if (id) {
				systemJobId = id;
			}
		} catch {
			// ignore malformed chunks
		}
	}
	if (!systemJobId) {
		throw new Error('Branding stream did not return a system job id');
	}
	return systemJobId;
}
